import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchDCREntries, deleteDCREntry } from "../../api/dcrService";
import styles from "./DCRReport.module.css";

function formatTo12Hour(value24) {
  if (!value24) return "";
  const [hours, minutes] = value24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
}

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
}

function truncateNote(value, maxLength = 60) {
  if (!value) return "";
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength).trim() + "...";
}

export default function DCRReport() {
  const { user } = useSelector((state) => state.auth);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ city: "", area: "", speciality: "", doctor: "", status: "" });
  const [selectedNote, setSelectedNote] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadRecords = async () => {
      if (!user?.uid) {
        if (isMounted) setLoading(false);
        return;
      }
      try {
        const data = await fetchDCREntries(user.uid);
        if (isMounted) setRecords(data.reverse());
      } catch (err) {
        if (isMounted) setError("Failed to load DCR reports.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadRecords();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const uniqueValues = (key) => {
    const values = records.map((r) => r[key]).filter(Boolean);
    return [...new Set(values)];
  };

  const filteredRecords = records.filter((record) => {
    return (
      (!filter.city || record.city === filter.city) &&
      (!filter.area || record.area === filter.area) &&
      (!filter.speciality || record.speciality === filter.speciality) &&
      (!filter.doctor || record.doctor === filter.doctor) &&
      (!filter.status || record.status === filter.status)
    );
  });

  const handleDelete = async (id) => {
    if (!user?.uid) return;
    const confirmed = window.confirm("Are you sure you want to delete this DCR entry?");
    if (!confirmed) return;

    try {
      await deleteDCREntry(user.uid, id);
      setRecords((prev) => prev.filter((record) => record.id !== id));
    } catch (err) {
      setError("Failed to delete DCR entry.");
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading DCR reports...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${styles.tableCard}`}>
        <div className={styles.header}>
          <h1>DCR Report</h1>
          <p>View all daily call reports submitted by the team.</p>
        </div>

        {error && <div className={styles.alertError}>{error}</div>}

        <div className={styles.filters}>
          <div className={styles.field}>
            <label htmlFor="cityFilter">Filter by City</label>
            <select id="cityFilter" name="city" value={filter.city} onChange={handleFilterChange}>
              <option value="">All Cities</option>
              {uniqueValues("city").map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="areaFilter">Filter by Area</label>
            <select id="areaFilter" name="area" value={filter.area} onChange={handleFilterChange}>
              <option value="">All Areas</option>
              {uniqueValues("area").map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="specialityFilter">Filter by Speciality</label>
            <select id="specialityFilter" name="speciality" value={filter.speciality} onChange={handleFilterChange}>
              <option value="">All Specialities</option>
              {uniqueValues("speciality").map((speciality) => (
                <option key={speciality} value={speciality}>
                  {speciality}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="doctorFilter">Filter by Doctor</label>
            <select id="doctorFilter" name="doctor" value={filter.doctor} onChange={handleFilterChange}>
              <option value="">All Doctors</option>
              {uniqueValues("doctor").map((doc) => (
                <option key={doc} value={doc}>
                  {doc}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="statusFilter">Filter by Status</label>
            <select id="statusFilter" name="status" value={filter.status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              {uniqueValues("status").map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => setFilter({ city: "", area: "", speciality: "", doctor: "", status: "" })}
          >
            Clear Filters
          </button>
        </div>

        <div className={styles.summary}>
          <span className={styles.summaryItem}>
            <strong>{filteredRecords.length}</strong> record(s) shown
          </span>
          <span className={styles.summaryItem}>
            <strong>{records.length}</strong> total record(s)
          </span>
        </div>

        <div className={styles.scrollHint}>Scroll horizontally to view all columns</div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>City</th>
                <th>Area</th>
                <th>Speciality</th>
                <th>Doctor</th>
                <th>Product</th>
                <th>Visit Type</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th>Submitted By</th>
                <th>Submitted At</th>
                <th className={styles.notesColumn}>Notes</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.city}</td>
                    <td>{record.area}</td>
                    <td>{record.speciality}</td>
                    <td>{record.doctor}</td>
                    <td>{Array.isArray(record.product) ? record.product.join(", ") : record.product}</td>
                    <td>{record.visitType}</td>
                    <td>{formatDate(record.date)}</td>
                    <td>{record.time ? formatTo12Hour(record.time) : "—"}</td>
                    <td>
                      <span className={`${styles.badge} ${styles[record.status?.toLowerCase()] || ""}`}>
                        {record.status}
                      </span>
                    </td>
                    <td>{record.submittedBy}</td>
                    <td>{record.submittedAt ? new Date(record.submittedAt).toLocaleString("en-IN") : "—"}</td>
                    <td className={styles.notesColumn}>
                      <div className={styles.notePreview} title={record.notes || ""}>
                        {truncateNote(record.notes, 50)}
                      </div>
                      {record.notes && record.notes.length > 50 && (
                        <button
                          type="button"
                          className={styles.viewNoteBtn}
                          onClick={() => setSelectedNote(record.notes)}
                        >
                          View
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => handleDelete(record.id)}
                        title="Delete entry"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" className={styles.emptyCell}>
                    No DCR records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedNote && (
        <div className={styles.modalOverlay} onClick={() => setSelectedNote(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Notes</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setSelectedNote(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalNoteText}>{selectedNote}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
